import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  UserIcon,
  DocumentArrowUpIcon,
  SparklesIcon,
  PencilSquareIcon,
  ArrowDownTrayIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import FileUploadZone from '../components/FileUploadZone';
import AudiogramAnalysisStatus from '../components/AudiogramAnalysisStatus';
import ReportEditorProfessional from '../components/ReportEditorProfessional';
import MeatusInspectionField from '../components/MeatusInspectionField'; // ✅ NOVO: Inspeção do meato (CFFA 2020)
import apiClient, { patientsService, examsService, reportsService } from '../services/api'; // Importar services
import * as authService from '../services/authService'; // Importar authService
import DashboardLayout from '../components/DashboardLayout';

const ReportWizard = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [wizardData, setWizardData] = useState({
    // Step 0: Patient & Doctor Selection
    patientId: '',
    doctorId: '',
    // Step 1: Consultation Type
    consultationType: '', // 'Eletiva' | 'Ocupacional' | 'Judicial / Pericial' | 'Infantil / Neo Natal'
    consultationReason: '', // só se consultationType === 'Ocupacional'
    // Step 2: Upload de Arquivos + Repouso Acústico
    repousoAcustico14h: false, // ✅ NOVO: checkbox "Houve pausa no ruído antes do exame?"
    uploadedFiles: [],
    // Step 3: Generated Report
    generatedReport: null,
    // Step 4: Edited Report
    editedReport: null,
  });

  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [currentDoctor, setCurrentDoctor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [examId, setExamId] = useState(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [reportDraft, setReportDraft] = useState('');
  // ✅ NOVO: Estados para inspeção do meato (CFFA 2020)
  const [meatusOD, setMeatusOD] = useState('');
  const [meatusOE, setMeatusOE] = useState('');
  // ✅ useRef para garantir execução única (mesmo em React StrictMode)
  const hasInitialized = useRef(false);

  const steps = [
    { id: 0, name: 'Paciente & Médico', icon: UserIcon },
    { id: 1, name: 'Tipo de Consulta', icon: DocumentTextIcon },
    { id: 2, name: 'Upload de Arquivos', icon: DocumentArrowUpIcon },
    { id: 3, name: 'Organizar Laudo', icon: SparklesIcon },
    { id: 4, name: 'Editar Laudo', icon: PencilSquareIcon },
    { id: 5, name: 'Exportar PDF', icon: ArrowDownTrayIcon },
  ];

  // Carregar dados iniciais
  useEffect(() => {
    const initializeWizard = async () => {
      // useRef persiste entre re-renders e não causa re-render quando muda
      if (hasInitialized.current) return;
      hasInitialized.current = true;
      
      // Carregar dados primeiro
      await loadPatients();
      
      // Validar perfil do médico
      const profileValid = await loadCurrentDoctor();
      if (!profileValid) return; // Se perfil inválido, para aqui
      
      // Validar equipamento
      await checkEquipmentBeforeStart();
    };
    
    initializeWizard();
  }, []);

  const checkEquipmentBeforeStart = async () => {
    try {
      const { equipmentsService } = await import('../services/api');
      const equipments = await equipmentsService.getAll();
      
      // Verificar se há pelo menos 1 equipamento ativo
      const activeEquipment = equipments.find(eq => eq.isAtivo);
      
      if (!activeEquipment) {
        toast.error(
          'Cadastre pelo menos um equipamento e deixe ele ativo antes de gerar laudos. Acesse Configurações > Equipamentos > + Novo Equipamento e certifique-se de deixar o Status Ativo.',
          { 
            id: 'equipment-missing-warning', // ID único para evitar duplicação
            icon: '🔧',
            duration: 10000 
          }
        );
        
        // Redirecionar para Settings > Equipamentos após 3 segundos
        setTimeout(() => {
          window.location.href = '/settings?tab=equipamentos';
        }, 3000);
        
        return;
      }

      // Verificar calibração (< 1 ano)
      const calibrationDate = new Date(activeEquipment.dataCalibracao);
      const daysSinceCalibration = Math.floor((Date.now() - calibrationDate.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysSinceCalibration > 365) {
        toast.error(
          `A calibração do seu audiômetro expirou há ${daysSinceCalibration - 365} dias. A Portaria 19 exige calibração anual obrigatória.`,
          { 
            id: 'calibration-expired-warning', // ID único para evitar duplicação
            icon: '⚠️',
            duration: 10000 
          }
        );
        
        // Redirecionar para Settings > Equipamentos após 3 segundos
        setTimeout(() => {
          window.location.href = '/settings?tab=equipamentos';
        }, 3000);
      }
    } catch (err) {
      console.error('Erro ao verificar equipamento:', err);
      toast.error(
        'Cadastre pelo menos um equipamento e deixe ele ativo antes de gerar laudos. Acesse Configurações > Equipamentos > + Novo Equipamento e certifique-se de deixar o Status Ativo.',
        { 
          id: 'equipment-error-warning', // ID único para evitar duplicação
          icon: '🔧',
          duration: 10000 
        }
      );
      
      setTimeout(() => {
        window.location.href = '/settings?tab=equipamentos';
      }, 3000);
    }
  };

  const loadPatients = async () => {
    try {
      const data = await patientsService.getAll();
      setPatients(data);
    } catch (err) {
      setError('Erro ao carregar lista de pacientes');
      console.error(err);
    }
  };

  const loadCurrentDoctor = async () => {
    try {
      // Usar authService.getCurrentUser() que já inclui o token JWT automaticamente
      const data = await authService.getCurrentUser();
      
      setCurrentDoctor(data);

      // Preencher automaticamente o médico logado no wizard
      setWizardData((prev) => ({ ...prev, doctorId: data.id }));

      // Adicionar médico logado à lista de médicos (apenas ele)
      setDoctors([data]);

      // Verificar se perfil está completo (ProfileCompleted é calculado automaticamente no backend)
      if (!data.profileCompleted) {
        toast.error(
          'Complete seu perfil médico antes de gerar laudos. Acesse Configurações > Perfil para preencher CRM, UF e especialização.',
          { 
            id: 'profile-incomplete-warning', // ID único para evitar duplicação
            icon: '👤',
            duration: 10000 
          }
        );
        
        // Redirecionar para Settings > Perfil após 3 segundos
        setTimeout(() => {
          window.location.href = '/settings?tab=perfil';
        }, 3000);
        
        return false; // Perfil incompleto
      }
      
      return true; // Perfil completo
    } catch (err) {
      setError('Erro ao carregar dados do médico logado');
      console.error(err);
      return false;
    }
  };

  const updateWizardData = (field, value) => {
    setWizardData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFilesUploaded = (uploadedFiles) => {
    setWizardData((prev) => ({
      ...prev,
      uploadedFiles: [...prev.uploadedFiles, ...uploadedFiles],
    }));
  };

  const createTemporaryExam = async () => {
    setLoading(true);
    try {
      const examData = await examsService.create({
        patientId: wizardData.patientId,
        date: new Date().toISOString(),
        operatorName: currentDoctor?.name || 'Operador',
        operatorRegistration: currentDoctor?.crm || '000000',
        consultationType: wizardData.consultationType || null, // Novo campo
        consultationReason: wizardData.consultationReason || null, // Novo campo
        ambiente: {
          ruidoDb: 0,
          calibracaoAtual: new Date().toISOString(),
          transdutor: 'TDH-39',
          },
          audiogram: {
            air: {},
            bone: null,
          },
          speechTest: {
            srtRe: null,
            srtLe: null,
            wrsRe: null,
            wrsLe: null,
          },
          observacoes: 'Exame temporário para upload de arquivos',
          // ✅ CFFA 2020: Inspeção NÃO enviada aqui (será preenchida no Step 2)
          meatusInspection: null
        });

      setExamId(examData.id);
      setCurrentStep(2); // Avançar para step 2 (Upload de Arquivos)
    } catch (err) {
      setError(`Erro ao criar exame: ${err.message}`);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ✅ NOVA FUNÇÃO: Atualizar exam com dados da inspeção do meato (CFFA 2020)
  const updateMeatusInspection = async () => {
    if (!examId || !meatusOD || !meatusOE) {
      console.error('❌ updateMeatusInspection: dados insuficientes', { examId, meatusOD, meatusOE });
      return false;
    }

    try {
      console.log('🔄 Atualizando inspeção do meato no exam:', examId);
      
      // Buscar exam atual
      const currentExam = await examsService.getById(examId);
      
      // 🔍 DEBUG: Verificar campos disponíveis
      console.log('🔍 DEBUG currentExam keys:', Object.keys(currentExam));
      console.log('🔍 DEBUG ambienteJson:', currentExam.ambienteJson);
      console.log('🔍 DEBUG ambiente:', currentExam.ambiente);
      
      // ✅ Construir payload correto (não fazer spread do exam retornado)
      // O backend espera CreateExamDto, que tem estrutura diferente do ExamDto
      
      // ✅ IMPORTANTE: ambienteJson contém os dados técnicos (ruidoDb, calibracaoAtual, transdutor)
      // NÃO usar currentExam.ambiente (que é a análise da IA com confidence/observations)
      let ambienteData;
      try {
        ambienteData = JSON.parse(currentExam.ambienteJson);
      } catch (err) {
        console.warn('⚠️ Falha ao parsear ambienteJson, usando dados padrão:', err);
        ambienteData = {
          ruidoDb: 0,
          calibracaoAtual: new Date().toISOString(),
          transdutor: 'TDH-39'
        };
      }
      
      const updatePayload = {
        patientId: currentExam.patientId,
        date: currentExam.date,
        operatorName: currentExam.operatorName,
        operatorRegistration: currentExam.operatorRegistration,
        consultationType: currentExam.consultationType || null,  // ✅ null em vez de undefined
        consultationReason: currentExam.consultationReason || null,  // ✅ null em vez de undefined
        // ✅ CORRIGIDO: usar ambienteJson (dados técnicos), não ambiente (análise IA)
        ambiente: ambienteData,
        // Audiogram retornado tem estrutura diferente, manter original
        audiogram: {
          air: currentExam.audiogram?.air || {},
          bone: currentExam.audiogram?.bone || null
        },
        speechTest: {
          srtRe: currentExam.speechTest?.srtRe || null,
          srtLe: currentExam.speechTest?.srtLe || null,
          wrsRe: currentExam.speechTest?.wrsRe || null,
          wrsLe: currentExam.speechTest?.wrsLe || null
        },
        observacoes: 'Exame temporário para upload de arquivos',
        // ✅ Adicionar inspeção do meato
        meatusInspection: {
          od: meatusOD,
          oe: meatusOE,
          inspectedBy: currentDoctor?.name || 'Operador',
          inspectedAt: new Date().toISOString()
        }
      };

      console.log('📤 Payload de atualização:', updatePayload);
      
      // ✅ LOG DETALHADO: tipos de cada campo
      console.log('🔍 TIPOS DOS CAMPOS:', {
        patientId: typeof updatePayload.patientId + ' = ' + updatePayload.patientId,
        date: typeof updatePayload.date + ' = ' + updatePayload.date,
        consultationType: updatePayload.consultationType === null ? 'null' : typeof updatePayload.consultationType,
        consultationReason: updatePayload.consultationReason === null ? 'null' : typeof updatePayload.consultationReason,
        ambiente: typeof updatePayload.ambiente + ' (keys: ' + Object.keys(updatePayload.ambiente).join(', ') + ')',
        audiogram: typeof updatePayload.audiogram + ' (keys: ' + Object.keys(updatePayload.audiogram).join(', ') + ')',
        meatusInspection: typeof updatePayload.meatusInspection + ' (keys: ' + Object.keys(updatePayload.meatusInspection).join(', ') + ')'
      });
      
      // ✅ LOG DETALHADO: JSON serializado (o que realmente será enviado)
      console.log('🔍 JSON SERIALIZADO:', JSON.stringify(updatePayload, null, 2));
      
      await examsService.update(examId, updatePayload);

      console.log('✅ Inspeção do meato atualizada com sucesso:', { od: meatusOD, oe: meatusOE });
      return true;
    } catch (err) {
      console.error('❌ Erro ao atualizar inspeção do meato:', err);
      console.error('❌ Detalhes do erro COMPLETO:', JSON.stringify(err.response?.data, null, 2));
      console.error('❌ Especialmente errors:', err.response?.data?.errors);
      toast.error('Erro ao salvar inspeção do meato. Tente novamente.');
      return false;
    }
  };

  const generateReport = async () => {
    // Proteção contra cliques duplos
    if (generatingReport) {
      console.log('⚠️ Geração já em andamento, ignorando clique duplicado');
      return;
    }

    setGeneratingReport(true);
    setError(null);

    try {
      if (!examId) {
        throw new Error('ExamId não encontrado. Por favor, refaça o upload dos arquivos.');
      }

      // ✅ NOVO: Preparar payload com repouso acústico 14h E inspeção do meato
      const payload = {
        consultationType: wizardData.consultationType || null,
        consultationReason: wizardData.consultationReason || null,
        repousoAcustico14h: wizardData.repousoAcustico14h, // ✅ NOVO campo
        // ✅ CFFA 2020: Adicionar inspeção do meato (preenchida no Step 2)
        meatusInspection: (meatusOD && meatusOE) ? {
          od: meatusOD,
          oe: meatusOE,
          inspectedBy: currentDoctor?.name || 'Operador',
          inspectedAt: new Date().toISOString()
        } : null
      };

      // Chamar API real para organizar laudo com IA
      const reportData = await reportsService.generate(examId, payload);
      
      // Extrair o texto do laudo do JSON retornado
      const reportText = reportData.draftJson || 'Laudo gerado com sucesso';
      const reportId = reportData.id; // ← SALVAR O ID DO REPORT!
      
      setWizardData((prev) => ({ 
        ...prev, 
        generatedReport: reportText,
        reportId: reportId // ← ADICIONAR AO ESTADO!
      }));
      setReportDraft(reportText);
      
      console.log('Report gerado com ID:', reportId); // Debug
      toast.success('Laudo gerado com sucesso pela IA!', {
        icon: '✨',
        duration: 3000,
      });
      setCurrentStep(4); // Ir para edição (Step 4)
    } catch (err) {
      setError(`Erro ao organizar laudo: ${err.message}`);
      console.error(err);
    } finally {
      setGeneratingReport(false);
    }
  };

  const saveEditedReport = () => {
    // Salvar sempre o reportDraft atual (editado ou não)
    const currentDraft = reportDraft || wizardData.generatedReport;
    setWizardData((prev) => ({ ...prev, editedReport: currentDraft }));
    setCurrentStep(5); // ✅ ir para Step 5 (Export PDF)
  };

  const exportPDF = async () => {
    if (!wizardData.reportId) {
      toast.error('Nenhum laudo encontrado para exportar', {
        duration: 4000,
      });
      return;
    }

    setLoading(true);
    const loadingToast = toast.loading('Gerando PDF...');

    try {
      // 🔥 NOVO: Baixar PDF diretamente do MinIO (já foi gerado automaticamente)
      console.log('📄 Baixando PDF do MinIO para report:', wizardData.reportId);

      // Usar apiClient (axios) que já tem interceptor JWT configurado
      const response = await apiClient.get(
        `/reports/${wizardData.reportId}/download`,
        { responseType: 'blob' } // Importante: informar que esperamos um blob
      );

      // Baixar o PDF
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `laudo_audiometrico_${wizardData.patient?.name?.replace(/\s+/g, '_') || 'paciente'}_${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.dismiss(loadingToast);
      toast.success('PDF exportado com sucesso!', {
        icon: '📄',
        duration: 3000,
      });
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      toast.dismiss(loadingToast);
      const errorMessage = error.response?.data?.error || error.message || 'Erro desconhecido';
      toast.error(`Erro ao exportar PDF: ${errorMessage}`, {
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const nextStep = async () => {
    // Validações por step
    if (currentStep === 0) {
      if (!wizardData.patientId || !wizardData.doctorId) {
        toast.error('Por favor, selecione o paciente e o médico', {
          icon: '⚠️',
          duration: 4000,
        });
        return;
      }
    }

    if (currentStep === 1) {
      // ✅ Step 1: Tipo de Consulta
      if (!wizardData.consultationType) {
        toast.error('Por favor, selecione o tipo de consulta', {
          icon: '⚠️',
          duration: 4000,
        });
        return;
      }
      // Se Ocupacional, validar consultationReason
      if (wizardData.consultationType === 'Ocupacional' && !wizardData.consultationReason) {
        toast.error('Por favor, selecione o motivo da consulta ocupacional', {
          icon: '⚠️',
          duration: 4000,
        });
        return;
      }
      
      // ✅ NOVO: Criar examId antes de ir para Step 2 (Upload)
      if (!examId) {
        createTemporaryExam();
        return;
      } else {
        setCurrentStep(2);
        return;
      }
    }

    if (currentStep === 2) {
      // ✅ VALIDAÇÃO: Inspeção do Meato (CFFA 2020)
      if (!meatusOD || !meatusOE) {
        toast.error('Por favor, preencha a inspeção do meato acústico externo para ambas as orelhas (OBRIGATÓRIO CFFA 2020)', {
          icon: '👂',
          duration: 5000,
        });
        return;
      }

      // ✅ Step 2: Upload de Arquivos
      if (wizardData.uploadedFiles.length === 0) {
        toast.error('Por favor, faça upload de pelo menos um arquivo', {
          icon: '📁',
          duration: 4000,
        });
        return;
      }
      
      // ✅ VALIDAÇÃO CRÍTICA: Verificar se houve pelo menos UMA análise bem-sucedida
      const hasSuccessfulAnalysis = wizardData.uploadedFiles.some(
        file => file.analysisSucceeded !== false
      );
      
      if (!hasSuccessfulAnalysis) {
        toast.error(
          '❌ Nenhum arquivo foi analisado com sucesso pela IA. Você precisa preencher os dados do audiograma manualmente antes de prosseguir.',
          {
            icon: '⚠️',
            duration: 8000,
          }
        );
        return;
      }
      
      // ✅ Avançar para Step 3 (Organizar Laudo)
      // Nota: meatusInspection será enviado na geração do report (Step 3)
      setCurrentStep(3);
      return;
    }

    if (currentStep === 3) {
      // ✅ Step 3: Organizar Laudo - gerar laudo com IA
      generateReport();
      return;
    }

    if (currentStep === 4) {
      // ✅ Step 4: Editar Laudo - salvar e ir para exportação
      saveEditedReport();
      return;
    }

    setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const prevStep = () => {
    // Navegação linear simples
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return wizardData.patientId && wizardData.doctorId;
      case 1:
        // Tipo de Consulta: obrigatório
        // Se Ocupacional, Motivo também é obrigatório
        if (!wizardData.consultationType) return false;
        if (wizardData.consultationType === 'Ocupacional' && !wizardData.consultationReason) return false;
        return true;
      case 2:
        // Upload de Arquivos: deve ter pelo menos 1 arquivo E pelo menos 1 análise bem-sucedida
        const hasSuccessfulAnalysis = wizardData.uploadedFiles.some(
          file => file.analysisSucceeded !== false
        );
        return wizardData.uploadedFiles.length > 0 && hasSuccessfulAnalysis;
      case 3:
        return wizardData.generatedReport;
      case 4:
        // Permite prosseguir se tiver laudo gerado OU editado
        return wizardData.editedReport || wizardData.generatedReport || reportDraft;
      default:
        return true;
    }
  };

  return (
    <DashboardLayout>
      <div className="bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8 px-4 min-h-screen">
        <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Assistente de Laudo Audiométrico</h1>
          <p className="text-gray-600">Siga os passos para organizar um laudo completo com IA</p>
        </div>

        {/* Progress Steps */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === index;
              const isCompleted = currentStep > index;
              const isDisabled = currentStep < index;

              // Ocultar step 2 se audiometria simples
              if (step.id === 2 && wizardData.audiometryType === 'simple') {
                return null;
              }

              return (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                        isActive
                          ? 'bg-gradient-to-br from-blue-500 to-purple-600 text-white shadow-lg scale-110'
                          : isCompleted
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-200 text-gray-400'
                      }`}
                    >
                      <Icon className="w-6 h-6" />
                    </div>
                    <span
                      className={`mt-2 text-xs font-medium ${
                        isActive ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-400'
                      }`}
                    >
                      {step.name}
                    </span>
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`flex-1 h-1 mx-2 rounded transition-all ${
                        isCompleted ? 'bg-green-500' : 'bg-gray-200'
                      }`}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Step 0: Patient & Doctor Selection */}
          {currentStep === 0 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Selecionar Paciente e Médico</h2>

              {/* Patient Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Paciente *</label>
                {patients.length === 0 ? (
                  <div className="text-red-600 bg-red-50 border border-red-200 rounded-lg p-4">
                    Nenhum paciente cadastrado. Por favor, cadastre um paciente primeiro.
                  </div>
                ) : (
                  <select
                    value={wizardData.patientId}
                    onChange={(e) => updateWizardData('patientId', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Selecione um paciente</option>
                    {patients.map((patient) => (
                      <option key={patient.id} value={patient.id}>
                        {patient.name} - {patient.age} anos ({patient.sex})
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Doctor Selection - Readonly (Médico Logado) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Médico Responsável *</label>
                {currentDoctor ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-900">
                      {currentDoctor.Name || currentDoctor.name}
                    </p>
                    {(currentDoctor.Crm || currentDoctor.crm) && (currentDoctor.CrmState || currentDoctor.crmState) ? (
                      <p className="text-sm text-gray-600 mt-1">
                        CRM: {currentDoctor.Crm || currentDoctor.crm}/{currentDoctor.CrmState || currentDoctor.crmState}
                        {(currentDoctor.Specialization || currentDoctor.specialty) && ` - ${currentDoctor.Specialization || currentDoctor.specialty}`}
                      </p>
                    ) : (
                      <p className="text-sm text-yellow-700 mt-1">
                        ⚠️ Complete seu perfil para organizar laudos válidos
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-800">
                    Carregando dados do médico...
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 1: Consultation Type Selection (NEW) */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Tipo de Consulta</h2>
              <p className="text-gray-600 mb-6">Selecione o tipo de consulta audiométrica</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Eletiva */}
                <button
                  onClick={() => {
                    setWizardData(prev => ({
                      ...prev,
                      consultationType: 'Eletiva',
                      consultationReason: '' // Limpar motivo ao mudar tipo
                    }));
                  }}
                  className={`p-6 border-2 rounded-xl transition-all text-left ${
                    wizardData.consultationType === 'Eletiva'
                      ? 'border-blue-500 bg-blue-50 shadow-lg ring-2 ring-blue-200'
                      : 'border-gray-300 hover:border-blue-300 hover:bg-blue-50'
                  }`}
                >
                  <div className="flex items-center mb-3">
                    <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                      wizardData.consultationType === 'Eletiva'
                        ? 'border-blue-500 bg-blue-500'
                        : 'border-gray-400'
                    }`}>
                      {wizardData.consultationType === 'Eletiva' && (
                        <CheckCircleIcon className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Eletiva</h3>
                  </div>
                  <p className="text-sm text-gray-600 ml-8">Consulta audiométrica eletiva ou de rotina</p>
                </button>

                {/* Ocupacional */}
                <button
                  onClick={() => {
                    setWizardData(prev => ({
                      ...prev,
                      consultationType: 'Ocupacional',
                      consultationReason: '' // Resetar motivo ao selecionar
                    }));
                  }}
                  className={`p-6 border-2 rounded-xl transition-all text-left ${
                    wizardData.consultationType === 'Ocupacional'
                      ? 'border-green-500 bg-green-50 shadow-lg ring-2 ring-green-200'
                      : 'border-gray-300 hover:border-green-300 hover:bg-green-50'
                  }`}
                >
                  <div className="flex items-center mb-3">
                    <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                      wizardData.consultationType === 'Ocupacional'
                        ? 'border-green-500 bg-green-500'
                        : 'border-gray-400'
                    }`}>
                      {wizardData.consultationType === 'Ocupacional' && (
                        <CheckCircleIcon className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Ocupacional</h3>
                  </div>
                  <p className="text-sm text-gray-600 ml-8">Exame audiométrico ocupacional (ASO)</p>
                </button>

                {/* Judicial / Pericial */}
                <button
                  onClick={() => {
                    setWizardData(prev => ({
                      ...prev,
                      consultationType: 'Judicial / Pericial',
                      consultationReason: ''
                    }));
                  }}
                  className={`p-6 border-2 rounded-xl transition-all text-left ${
                    wizardData.consultationType === 'Judicial / Pericial'
                      ? 'border-purple-500 bg-purple-50 shadow-lg ring-2 ring-purple-200'
                      : 'border-gray-300 hover:border-purple-300 hover:bg-purple-50'
                  }`}
                >
                  <div className="flex items-center mb-3">
                    <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                      wizardData.consultationType === 'Judicial / Pericial'
                        ? 'border-purple-500 bg-purple-500'
                        : 'border-gray-400'
                    }`}>
                      {wizardData.consultationType === 'Judicial / Pericial' && (
                        <CheckCircleIcon className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Judicial / Pericial</h3>
                  </div>
                  <p className="text-sm text-gray-600 ml-8">Exame para fins judiciais ou periciais</p>
                </button>

                {/* Infantil / Neo Natal */}
                <button
                  onClick={() => {
                    setWizardData(prev => ({
                      ...prev,
                      consultationType: 'Infantil / Neo Natal',
                      consultationReason: ''
                    }));
                  }}
                  className={`p-6 border-2 rounded-xl transition-all text-left ${
                    wizardData.consultationType === 'Infantil / Neo Natal'
                      ? 'border-orange-500 bg-orange-50 shadow-lg ring-2 ring-orange-200'
                      : 'border-gray-300 hover:border-orange-300 hover:bg-orange-50'
                  }`}
                >
                  <div className="flex items-center mb-3">
                    <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                      wizardData.consultationType === 'Infantil / Neo Natal'
                        ? 'border-orange-500 bg-orange-500'
                        : 'border-gray-400'
                    }`}>
                      {wizardData.consultationType === 'Infantil / Neo Natal' && (
                        <CheckCircleIcon className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Infantil / Neo Natal</h3>
                  </div>
                  <p className="text-sm text-gray-600 ml-8">Exame audiométrico pediátrico ou neonatal</p>
                </button>
              </div>

              {/* Consultation Reason - Only if Ocupacional */}
              {wizardData.consultationType === 'Ocupacional' && (
                <div className="mt-8 p-6 bg-green-50 border-2 border-green-200 rounded-xl">
                  <label className="block text-sm font-medium text-gray-900 mb-4">
                    Motivo da Consulta Ocupacional *
                  </label>
                  <div className="space-y-3">
                    {['Admissional', 'Periódica', 'Retorno ao trabalho', 'Mudança de função', 'Demissional'].map((reason) => (
                      <label
                        key={reason}
                        className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          wizardData.consultationReason === reason
                            ? 'border-green-500 bg-white shadow-md'
                            : 'border-green-200 hover:border-green-400 hover:bg-white'
                        }`}
                      >
                        <input
                          type="radio"
                          name="consultationReason"
                          value={reason}
                          checked={wizardData.consultationReason === reason}
                          onChange={(e) => updateWizardData('consultationReason', e.target.value)}
                          className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500"
                        />
                        <span className={`ml-3 text-sm font-medium ${
                          wizardData.consultationReason === reason
                            ? 'text-green-900'
                            : 'text-gray-700'
                        }`}>
                          {reason}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Upload de Arquivos + Repouso Acústico */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Upload de Arquivos</h2>
              <p className="text-gray-600 mb-6">
                Faça upload de exames audiométricos (PDF, JPEG, PNG). 
                <strong className="text-blue-600"> Nossa IA irá extrair automaticamente os dados!</strong>
              </p>

              {/* ✅ NOVO: Checkbox Repouso Acústico 14h */}
              <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 mb-6">
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    checked={wizardData.repousoAcustico14h}
                    onChange={(e) => updateWizardData('repousoAcustico14h', e.target.checked)}
                    className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="ml-3">
                    <span className="block text-sm font-semibold text-gray-900">
                      Houve pausa no ruído antes do exame audiométrico (repouso acústico de 14 horas)?
                    </span>
                    <span className="block text-xs text-gray-600 mt-1">
                      ✅ Importante: Esta informação, junto com o equipamento cadastrado e a calibração, 
                      será usada pela IA para gerar o laudo de acordo com a NR-7 e CFFA 2020.
                    </span>
                  </span>
                </label>
              </div>

              {/* ✅ NOVO: Inspeção do Meato Acústico Externo (CFFA 2020) */}
              <MeatusInspectionField
                odValue={meatusOD}
                oeValue={meatusOE}
                onChange={(od, oe) => {
                  setMeatusOD(od);
                  setMeatusOE(oe);
                }}
                required={true}
              />

              {/* File Upload Zone */}
              {examId && currentDoctor ? (
                <FileUploadZone
                  onFilesUploaded={handleFilesUploaded}
                  examId={examId}
                  userId={currentDoctor.id}
                />
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-800">
                  Preparando ambiente de upload...
                </div>
              )}

              {wizardData.uploadedFiles.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-800 font-medium">
                    ✅ {wizardData.uploadedFiles.length} arquivo(s) enviado(s) com sucesso!
                  </p>
                </div>
              )}

              {/* Status da Análise Automática por IA */}
              {examId && wizardData.uploadedFiles.length > 0 && (
                <AudiogramAnalysisStatus examId={examId} />
              )}
            </div>
          )}

          {/* Step 3: Generate Report */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Organizar Laudo</h2>
              <p className="text-gray-600 mb-6">
                Nossa IA irá processar os dados do audiograma e organizar um laudo completo seguindo as normas CFFA 2020 e NR-7.
              </p>

              <div className="flex justify-center">
                <button
                  onClick={generateReport}
                  disabled={generatingReport}
                  className="px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-semibold shadow-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                >
                  {generatingReport ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Organizando laudo...
                    </>
                  ) : (
                    <>
                      <SparklesIcon className="w-6 h-6" />
                      Organizar Laudo com IA
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Edit Report */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <ReportEditorProfessional 
                reportData={reportDraft}
                onSave={(editedData) => {
                  const jsonString = JSON.stringify(editedData, null, 2);
                  setReportDraft(jsonString);
                  setWizardData((prev) => ({ ...prev, editedReport: jsonString }));
                }}
              />
            </div>
          )}

          {/* Step 5: Export PDF */}
          {currentStep === 5 && (
            <div className="text-center py-12 space-y-6">
              <CheckCircleIcon className="w-24 h-24 text-green-500 mx-auto" />
              <h2 className="text-3xl font-bold text-gray-900">Laudo Finalizado!</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Seu laudo audiométrico foi gerado com sucesso. Clique no botão abaixo para exportar o PDF
                e obter o documento para assinatura médica.
              </p>

              <div className="bg-green-50 border border-green-200 rounded-lg p-6 max-w-2xl mx-auto">
                <h3 className="font-semibold text-gray-900 mb-2">Resumo do Laudo:</h3>
                <ul className="text-left text-sm text-gray-700 space-y-1">
                  <li>✅ Paciente: {patients.find((p) => p.id === wizardData.patientId)?.name}</li>
                  <li>✅ Médico: {doctors.find((d) => d.id === wizardData.doctorId)?.name}</li>
                  <li>✅ Tipo: {wizardData.audiometryType === 'nexo-causal' ? 'Com Nexo Causal' : 'Simples'}</li>
                  <li>✅ Arquivos anexados: {wizardData.uploadedFiles.length}</li>
                </ul>
              </div>

              <button
                onClick={exportPDF}
                className="px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg font-medium hover:from-green-600 hover:to-emerald-700 shadow-lg transition-all inline-flex items-center gap-2"
              >
                <ArrowDownTrayIcon className="w-6 h-6" />
                Exportar PDF para Assinatura
              </button>

              <button
                onClick={() => {
                  if (window.confirm('Deseja iniciar um novo laudo? Os dados atuais serão perdidos.')) {
                    window.location.reload();
                  }
                }}
                className="block mx-auto mt-4 text-blue-600 hover:text-blue-800 font-medium"
              >
                Iniciar Novo Laudo
              </button>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          <button
            onClick={prevStep}
            disabled={currentStep === 0}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
              currentStep === 0
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-white text-gray-700 border-2 border-gray-300 hover:bg-gray-50 shadow-md'
            }`}
          >
            <ChevronLeftIcon className="w-5 h-5" />
            Voltar
          </button>

          <button
            onClick={nextStep}
            disabled={!canProceed() || currentStep === steps.length - 1}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
              canProceed() && currentStep < steps.length - 1
                ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg hover:from-blue-600 hover:to-purple-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            Próximo
            <ChevronRightIcon className="w-5 h-5" />
          </button>
        </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ReportWizard;
